import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileStack, Info, Lightbulb, Upload } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday, shortDate } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ValueUpload } from "@/components/value/ValueUpload";
import { MARKET_LABEL } from "@/components/value/valueFormat";
import { ContextPanel } from "@/components/value/manager/ContextPanel";
import { QuoteTable } from "@/components/value/manager/QuoteTable";
import { ShotHistory, shotImage } from "@/components/value/manager/ShotHistory";
import { EditDialog, VersionsDialog } from "@/components/value/manager/QuoteDialogs";
import { MARKET_NAME, SORTS, buildRows, fmtTime, gameLabel, milestone, rowState, shotsFor } from "@/components/value/manager/omUtils";
import "@/components/value/manager/OddsManager.css";

const shift = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

function useManager(day) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/value/manager", { params: { day } }).then((r) => setData(r.data)).catch((e) => toast.error(errorText(e))), [day]);
  useEffect(() => { setData(null); load(); }, [load]);
  return [data, load];
}

// Points: players the model sees at 18+ whose ladder stops below 30+ (the 30+ screenshot is probably missing).
function MissingWarn({ market, rows }) {
  if (market !== "pts") return null;
  const by = {};
  rows.filter((r) => !r.empty && r.variant !== "line").forEach((r) => {
    const d = r.over || r.under;
    const k = `${r.player_id}|${r.bookmaker}`;
    by[k] = { ...d, top: Math.max(by[k]?.top ?? 0, r.line) };
  });
  const items = Object.values(by).filter((p) => (p.proj ?? 0) >= 18 && p.top < 29.5);
  if (!items.length) return null;
  return (
    <div className="om-warn" data-testid="om-missing-30">
      <b>{`MISSING 30+ · ${items.length} ${items.length > 1 ? "players" : "player"} projected 18+ pts without the 30+ milestones`}</b>
      <div>{items.map((p) => <span key={`${p.player_id}${p.bookmaker}`}>{p.player_name} <small>{p.bookmaker} · proj {p.proj?.toFixed(1)} · up to {milestone(p.top)}+</small></span>)}</div>
    </div>
  );
}

function Metrics({ shots, rows, last }) {
  const n = (s) => rows.filter((r) => rowState(r) === s).length;
  return (
    <div className="om-metrics" data-testid="om-metrics">
      <div><b data-testid="om-total-shots">{shots}</b><small>Total Screenshots</small></div>
      <div><b data-testid="om-total-milestones">{rows.filter((r) => !r.empty).length}</b><small>Total Milestones</small></div>
      <div><b className="om-last">{fmtTime(last)}</b><small>Last Update</small></div>
      <ul>
        <li><i className="ok" />Have odds (verified)<b>{n("verified")}</b></li>
        <li><i className="warn" />Have odds (not verified)<b>{n("pending")}</b></li>
        <li><i className="none" />No odds yet<b>{n("none")}</b></li>
      </ul>
    </div>
  );
}

export default function OddsManager() {
  const [params] = useSearchParams();
  const [day, setDay] = useState(params.get("day") || nbaToday());
  const [data, load] = useManager(day);
  const [ctx, setCtx] = useState({ game: params.get("game") || "", player: "", book: "", market: "pts" });
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("asc");
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [versions, setVersions] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const pick = useRef(null);
  const games = useMemo(() => data?.games || [], [data]);
  const lines = useMemo(() => data?.lines || [], [data]);
  const shots = useMemo(() => data?.screenshots || [], [data]);
  useEffect(() => {  // default to the first game that has odds, else the first game
    if (games.length && !games.some((g) => g.id === ctx.game)) {
      setCtx((c) => ({ ...c, game: (games.find((g) => lines.some((l) => l.game_id === g.id)) || games[0]).id, player: "" }));
    }
  }, [games, lines, ctx.game]);
  const shotNo = useMemo(() => Object.fromEntries(shots.map((s) => [s.id, s.n])), [shots]);
  const names = useMemo(() => Object.fromEntries((data?.players || []).map((p) => [p.id, p.name])), [data]);
  const all = useMemo(() => buildRows(lines, ctx), [lines, ctx]);
  const rows = useMemo(() => all.filter((r) => status === "all" || rowState(r) === status).sort(SORTS[sort]), [all, status, sort]);
  const ctxShots = useMemo(() => shotsFor(shots, ctx), [shots, ctx]);
  const game = games.find((g) => g.id === ctx.game);
  const player = (data?.players || []).find((p) => p.id === ctx.player);
  const last = all.reduce((m, r) => { const t = (r.over || r.under)?.scanned_at || ""; return t > m ? t : m; }, "");
  const openShot = (id) => { const s = shots.find((x) => x.id === id); if (s?.has_image) setView(s); else toast.info("Image not stored for this screenshot"); };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errorText(e)); } };
  const verify = (r) => act(() => Promise.all([r.over, r.under].filter((d) => d?.check).map((d) => api.post(`/value/picks/${d.id}/confirm`))), "Odds verified");
  const save = (r, vals) => { setEdit(null); act(() => Promise.all(["over", "under"].filter((s) => r[s] && Number(vals[s]) !== r[s].odds)
    .map((s) => api.put(`/value/picks/${r[s].id}/odds`, { odds: Number(vals[s]) }))), "Odds updated"); };
  const run = async () => {
    const c = confirm; setConfirm(null);
    if (c.type === "shot") {
      act(() => api.delete(`/value/screenshots/${c.item.id}`).then((r) => toast.message(`${r.data.reverted} back to an earlier price · ${r.data.removed} removed`)), `Screenshot #${c.item.n} deleted`);
    } else {
      act(() => Promise.all([c.item.over, c.item.under].filter(Boolean).map((d) => api.delete(`/value/picks/${d.id}`))), "Milestone removed");
    }
  };
  const chips = [["all", "All", all.length], ["verified", "Verified", all.filter((r) => rowState(r) === "verified").length],
    ["pending", "Not Verified", all.filter((r) => rowState(r) === "pending").length], ["none", "No Odds", all.filter((r) => rowState(r) === "none").length]];
  return (
    <div className="om-root" data-testid="odds-manager">
      <nav className="om-crumbs"><Link to={`/?day=${day}`} data-testid="om-back">Value Scanner</Link><ChevronRight size={13} /><span>Odds Upload Manager</span></nav>
      <header className="om-head">
        <span className="om-head-icon"><FileStack size={26} /></span>
        <div><h1>ODDS UPLOAD <span>MANAGER</span></h1><p>Upload screenshots, manage odds and track updates by player, game and market.</p></div>
        <div className="om-day">
          <button type="button" onClick={() => setDay(shift(day, -1))} aria-label="Previous day" data-testid="om-prev-day"><ChevronLeft size={16} /></button>
          <span data-testid="om-day">{shortDate(day)}</span>
          <button type="button" onClick={() => setDay(shift(day, 1))} aria-label="Next day" data-testid="om-next-day"><ChevronRight size={16} /></button>
        </div>
      </header>
      {!data ? <p className="om-empty">Loading…</p> : !games.length ? <p className="om-empty" data-testid="om-no-games">No games on this day.</p> : (
        <div className="om-layout">
          <ContextPanel ctx={ctx} set={setCtx} games={games} players={data.players} shots={shots} lines={lines} />
          <section className="om-panel om-main" data-testid="om-main">
            <div className="om-mainhead">
              <div>
                <h2 data-testid="om-title">{MARKET_NAME[ctx.market]} ({MARKET_LABEL[ctx.market]}) — <span>{player?.name || "All players"}</span></h2>
                <p className="om-ctx"><Info size={14} />Game: {gameLabel(game)} · Date: {shortDate(day)} · Player: {player?.name || "All"} · Bookmaker: {ctx.book || "All"}</p>
              </div>
              <button type="button" className="om-btn primary" onClick={() => pick.current?.()} disabled={game?.status !== "pre"} data-testid="om-upload-new"><Upload size={16} />Upload New Screenshot</button>
            </div>
            <Metrics shots={ctxShots.length} rows={all} last={last} />
            <div className="om-toolbar">
              {chips.map(([k, label, n]) => (
                <button key={k} type="button" className={`om-fchip ${k} ${status === k ? "is-active" : ""}`} onClick={() => setStatus(k)} data-testid={`om-status-${k}`}>{label} ({n})</button>
              ))}
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="om-sort" data-testid="om-sort">
                <option value="asc">Sort by Milestone (Low → High)</option>
                <option value="desc">Sort by Milestone (High → Low)</option>
                <option value="player">Sort by Player</option>
                <option value="latest">Sort by Latest Update</option>
              </select>
            </div>
            <MissingWarn market={ctx.market} rows={all} />
            <QuoteTable rows={rows} showPlayer={!ctx.player} shotNo={shotNo} onOpenShot={openShot} onVerify={verify}
              onEdit={setEdit} onHistory={setVersions} onDelete={(r) => setConfirm({ type: "row", item: r })} />
            <div className="om-note" data-testid="om-note">
              <Lightbulb size={22} />
              <div><b>Important</b><ul>
                <li>Each milestone keeps its own odds, per player, bookmaker and Over/Under.</li>
                <li>New screenshots update only the matching milestones; every other milestone stays unchanged.</li>
                <li>All screenshots and earlier prices stay in the history (eye icon).</li>
                <li>Only verified odds are used in the Value Scanner and Kelly; suspicious ones wait for Verify.</li>
              </ul></div>
            </div>
          </section>
          <ShotHistory shots={ctxShots} market={ctx.market} game={game} names={names} onView={setView} onDelete={(s) => setConfirm({ type: "shot", item: s })}>
            <ValueUpload day={day} games={games} selectedId={ctx.game} contextMarket={ctx.market} contextBook={ctx.book} inputRef={pick} onResult={() => load()} />
          </ShotHistory>
        </div>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="om-viewer" data-testid="om-viewer">
          <DialogTitle>Screenshot #{view?.n} · {view?.bookmaker} · {view && fmtTime(view.uploaded_at)}</DialogTitle>
          {view && <img src={shotImage(view.id)} alt={`Screenshot ${view.n}`} />}
        </DialogContent>
      </Dialog>
      <EditDialog row={edit} onClose={() => setEdit(null)} onSave={save} />
      <VersionsDialog row={versions} shotNo={shotNo} onClose={() => setVersions(null)} onOpenShot={(id) => { setVersions(null); openShot(id); }} />
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="om-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.type === "shot" ? `Delete screenshot #${confirm.item.n}?` : "Remove this milestone?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.type === "shot"
              ? "Its prices go back to the latest earlier screenshot of the same milestone; milestones only in this screenshot are removed."
              : "The active over/under price of this milestone is removed from the Value Scanner."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="om-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="om-confirm-ok">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
